import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { Filter, SearchService } from '@docpath/linces-angular-components/common';
import { DpFormModule, DpHeaderModule, DpSearchConfig, DpSearchModule, DpSection, DpSectionIcon, HeaderAction, MoreMenuGroup, SearchBasicInfo, SearchMode, SearchOptionType, SearchRowInfo } from '@docpath/webcomponents';
import { faGear } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { CloudService } from 'src/app/shared/services/cloud.service';

import { PageHeaderActionsService } from '../../../../../shared/components/header/services/page-header-actions.service';
import { Alias } from '../../models/alias';
import { AliasService } from '../../services/alias.service';
import { AliasTableService } from '../table/alias-table.service';
import { AliasHeaderService } from './alias-header.service';

@Component({
  selector: 'app-alias-header',
  templateUrl: './alias-header.component.html',
  standalone: true,
  imports: [DpHeaderModule, DpSearchModule, DpFormModule]
})
export class AliasHeaderComponent implements OnChanges, OnInit, OnDestroy {
  @Input() alias: Alias[] = [];
  @Output() filtersChanged = new EventEmitter<Filter[]>();

  leftActions: HeaderAction[] = [];
  rightActions: HeaderAction[] = [];
  moreMenuGroups: MoreMenuGroup[] = [];

  showSearch = false;
  searchConfig = new DpSearchConfig({
    resetOnClose: true,
    showMinimizeButton: true
  });
  private readonly title = 'alias-config.alias-row.title';
  private readonly tooltip = 'alias-config.alias-row.tooltip';
  section = new DpSection({ name: 'alias', rows: [], header: this.title, tooltip: this.tooltip, icon: new DpSectionIcon(faGear) });
  searchAvailableOptions: SearchRowInfo[] = [];
  private filters: Filter[] = [];
  private aliasChangedSubscription?: Subscription;
  private languageChangeSubscription?: Subscription;


  private readonly aliasHeaderService = inject(AliasHeaderService);
  private readonly aliasTableService = inject(AliasTableService);
  private readonly aliasService = inject(AliasService);
  private readonly pageHeaderActionsService = inject(PageHeaderActionsService);
  private readonly searchService = inject(SearchService);
  private readonly cloudService = inject(CloudService);
  private readonly translateService = inject(TranslateService);

  constructor() {
    this.searchAvailableOptions = this.getSearchAvailableOptions();

    this.rightActions = this.aliasHeaderService.getRightActions();
    const searchAction = this.aliasHeaderService.getSearchAction(this.rightActions);

    searchAction.action = () => {
      this.showSearch = !this.showSearch;
      this.updateSearchStyle();
    };

    this.updateSearchStyle();
  }

  private getSearchAvailableOptions(): SearchRowInfo[] {

    const searchAvailableOptions: SearchRowInfo[] = [];

    searchAvailableOptions.push(new SearchRowInfo(true, 'alert_alias', undefined, SearchOptionType.TEXT_FULL, SearchMode.exactly, true, true));
    searchAvailableOptions.push(new SearchRowInfo(false, 'email', undefined, SearchOptionType.TEXT_FULL, SearchMode.exactly, true, true));
    if (!this.cloudService.isModeCloud()) {
      searchAvailableOptions.push(new SearchRowInfo(false, 'phone_number', undefined, SearchOptionType.TEXT_FULL, SearchMode.exactly, true, true));
    }

    return searchAvailableOptions;

  }

  ngOnInit(): void {
    if (!this.aliasChangedSubscription) {
      this.aliasChangedSubscription = this.aliasService.aliasChanged.subscribe(() => {
        this.leftActions = [];
        this.moreMenuGroups = [];
      });
    }

    this.languageChangeSubscription ??= this.translateService.onLangChange.subscribe(() => {
      this.updateSearchStyle();
    });
  }

  ngOnDestroy(): void {
    if (this.aliasChangedSubscription) {
      this.aliasChangedSubscription.unsubscribe();
    }

    this.languageChangeSubscription?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // eslint-disable-next-line dot-notation
    if (changes['alias']) {
      this.leftActions = this.aliasHeaderService.getLeftActions(this.alias);
      const moreMenuGroupsActions = this.aliasHeaderService.getMoreActions(this.alias);
      this.moreMenuGroups = moreMenuGroupsActions.length > 0 ? [{ actions: moreMenuGroupsActions }] : [];
    }
  }

  isSearchVisible(): boolean {
    return this.showSearch;
  }

  clearSearch(): void {
    this.filters = [];
    this.filtersChanged.emit(this.filters);
    this.aliasTableService.loadAlias(this.filters, 1);
    this.leftActions = [];
    this.moreMenuGroups = [];
    this.updateSearchStyle(0);
  }

  sendSearch(currentFilters: Array<SearchBasicInfo>): void {
    this.filters = this.searchService.toFilters(currentFilters);
    this.filtersChanged.emit(this.filters);
    this.aliasTableService.loadAlias(this.filters, 1);
    this.leftActions = [];
    this.moreMenuGroups = [];
    this.updateSearchStyle();
  }

  hideSearch(): void {
    this.showSearch = false;
  }

  private updateSearchStyle(filtersCount?: number): void {
    const filterKeys = new Set(this.filters.map(filter => filter.key)),
      count = filtersCount ?? filterKeys.size;

    this.pageHeaderActionsService.updateSearchStyle(this.rightActions, count, 'search');
  }
}
